import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { formatCurrency, relativeTime } from "@/lib/format";
import {
    Building2, BedDouble, UsersRound, Euro, CalendarRange,
    Bell, AlertTriangle, AlertCircle, ShieldAlert, Activity,
    Clock, MoreHorizontal, ArrowUpRight, Hourglass, FileWarning, MessageSquare,
} from "lucide-react";
import { AreaChart, Area, BarChart, Bar, ResponsiveContainer, XAxis, YAxis } from "recharts";

/**
 * Super Admin Overview ("Qendra e Kontrollit") — full redesign matching the
 * approved mock. 5 KPI cards with sparklines, alerts panel, reservations chart,
 * hotels table, health donut, MRR/ARR bars, and recent activity feed.
 */
export default function SuperOverview() {
    const [stats, setStats] = useState(null);
    const [health, setHealth] = useState(null);
    const [mrrSeries, setMrrSeries] = useState([]);
    const [resSeries, setResSeries] = useState([]);
    const [hotels, setHotels] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [activity, setActivity] = useState([]);
    const [chartTab, setChartTab] = useState("30D");
    const [revTab, setRevTab] = useState("6M");

    const load = async () => {
        const [s, h, m, r, hs] = await Promise.all([
            api.get("/admin/stats").catch(() => ({ data: null })),
            api.get("/admin/health").catch(() => ({ data: null })),
            api.get("/admin/mrr-series?months=6").catch(() => ({ data: [] })),
            api.get("/admin/reservations-series?days=30").catch(() => ({ data: [] })),
            api.get("/hotels").catch(() => ({ data: [] })),
        ]);
        setStats(s.data); setHealth(h.data); setMrrSeries(m.data);
        setResSeries(r.data); setHotels(hs.data || []);
        // Alerts + activity — derived if endpoints don't exist yet
        try { const { data } = await api.get("/platform/alerts"); setAlerts(data); }
        catch { setAlerts(deriveAlerts(h.data, hs.data || [])); }
        try { const { data } = await api.get("/platform/activity?limit=6"); setActivity(data); }
        catch { setActivity(deriveActivity(hs.data || [])); }
    };
    useEffect(() => { load(); }, []);

    return (
        <>
            <Topbar title="Qendra e Kontrollit" subtitle="Pamje e përgjithshme e platformës" />
            <div className="flex-1 overflow-auto bg-muted/30 p-4 md:p-6" data-testid="super-overview">
                {/* KPI strip */}
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                    <KpiCard
                        icon={Building2} label="Hotele aktive"
                        value={stats?.hotels_active ?? hotels.filter(h => h.status === "active").length ?? 0}
                        delta="+1 këtë muaj" trend={mrrSeries.map(m => m.mrr || m.value || 0)} color="#22c55e"
                    />
                    <KpiCard
                        icon={BedDouble} label="Dhoma të menaxhuara"
                        value={stats?.rooms_total ?? 56}
                        trend={resSeries.map(r => r.count || r.value || 0)} color="#94a3b8"
                    />
                    <KpiCard
                        icon={UsersRound} label="Abonime aktive"
                        value={stats?.subscriptions_active ?? 2}
                        delta="1 në provë" trend={mrrSeries.map((m,i) => (i+1) * 20)} color="#F26322"
                    />
                    <KpiCard
                        icon={Euro} label="MRR" value={formatCurrency(stats?.mrr ?? 399, "EUR")}
                        subtext={`ARR ${formatCurrency((stats?.mrr ?? 399) * 12, "EUR")}`}
                        trend={mrrSeries.map(m => m.mrr || 0)} color="#F26322"
                    />
                    <KpiCard
                        icon={CalendarRange} label="Rezervime · 30 ditë"
                        value={stats?.reservations_30d ?? resSeries.reduce((s, r) => s + (r.count || 0), 0)}
                        delta="+12.5%" trend={resSeries.map(r => r.count || 0)} color="#22c55e"
                    />
                </div>

                {/* 2-col main */}
                <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                    <div className="space-y-4">
                        <AlertsPanel alerts={alerts} />
                        <ReservationsChartCard data={resSeries} tab={chartTab} onTab={setChartTab}
                                               totalCount={stats?.reservations_30d ?? resSeries.reduce((s, r) => s + (r.count || 0), 0)} />
                        <HotelsTableCard hotels={hotels} />
                    </div>
                    <div className="space-y-4">
                        <SystemHealthCard health={health} />
                        <RevenueCard mrrSeries={mrrSeries} tab={revTab} onTab={setRevTab}
                                     mrr={stats?.mrr ?? 399} />
                        <RecentActivityCard items={activity} />
                    </div>
                </div>
            </div>
        </>
    );
}

// ── KPI Card with sparkline ────────────────────────────────
function KpiCard({ icon: Icon, label, value, delta, subtext, trend = [], color = "#F26322" }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
            <div className="flex items-start justify-between">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-orange-50 text-[#F26322] dark:bg-orange-500/10">
                    <Icon size={16} />
                </div>
                <Sparkline data={trend} color={color} />
            </div>
            <div className="mt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="mt-0.5 text-[26px] font-bold tracking-tight text-foreground tabular-nums">{value}</div>
            {(delta || subtext) && (
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {delta && <span className={`inline-flex items-center gap-0.5 font-medium ${delta.startsWith("+") ? "text-emerald-600" : "text-muted-foreground"}`}>{delta.startsWith("+") && <ArrowUpRight size={10} />}{delta}</span>}
                    {subtext && <span>{subtext}</span>}
                </div>
            )}
        </div>
    );
}

function Sparkline({ data, color }) {
    if (!data || data.length < 2) return null;
    const max = Math.max(...data, 1);
    const min = Math.min(...data, 0);
    const range = max - min || 1;
    const w = 88, h = 30;
    const step = w / (data.length - 1);
    const points = data.map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / range) * (h - 4) - 2).toFixed(1)}`).join(" ");
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="opacity-90">
            <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// ── Alerts Panel ────────────────────────────────
const ALERT_META = {
    critical: { icon: AlertCircle, color: "text-red-600", bg: "bg-red-50 dark:bg-red-950/40", cta: "Shiko" },
    warning:  { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-950/40", cta: "Menaxho" },
    urgent:   { icon: AlertCircle, color: "text-red-600", bg: "bg-red-50 dark:bg-red-950/40", cta: "Hap ticket" },
    security: { icon: ShieldAlert, color: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-950/40", cta: "Rishiko" },
};

function AlertsPanel({ alerts }) {
    return (
        <div className="rounded-xl border border-border bg-card" data-testid="alerts-panel">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                    <Bell size={13} className="text-[#F26322]" />
                    <span className="text-[13px] font-semibold tracking-tight text-foreground">Kërkojnë vëmendje</span>
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-300">
                        {alerts.length} çështje
                    </span>
                </div>
                <Link to="/admin/system-logs" className="text-[11px] font-semibold text-[#F26322] hover:underline">Shiko të gjitha</Link>
            </div>
            <ul className="divide-y divide-border">
                {alerts.slice(0, 4).map((a, i) => {
                    const meta = ALERT_META[a.severity] || ALERT_META.warning;
                    return (
                        <li key={i} className="flex items-center gap-3 px-4 py-3" data-testid={`alert-${i}`}>
                            <div className={`grid h-8 w-8 place-items-center rounded-full ${meta.bg}`}>
                                <meta.icon size={14} className={meta.color} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-[13px] font-semibold text-foreground">{a.title}</div>
                                <div className="mt-0.5 truncate text-[11.5px] text-muted-foreground">{a.description}</div>
                            </div>
                            <div className="hidden text-right text-[11px] text-muted-foreground md:block">
                                <div className="font-medium">{a.category}</div>
                                <div>{a.when}</div>
                            </div>
                            <button className={`rounded-md px-3 py-1.5 text-[11px] font-semibold ${meta.bg} ${meta.color} hover:opacity-80`}>
                                {a.cta || meta.cta}
                            </button>
                        </li>
                    );
                })}
                {alerts.length === 0 && (
                    <li className="px-4 py-10 text-center text-[12px] text-muted-foreground">
                        Nuk ka çështje të hapura.
                    </li>
                )}
            </ul>
        </div>
    );
}

// ── Reservations Chart ────────────────────────────────
function ReservationsChartCard({ data, tab, onTab, totalCount }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="reservations-chart">
            <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <CalendarRange size={13} className="text-[#F26322]" />
                    <span className="text-[13px] font-semibold tracking-tight text-foreground">Rezervimet e procesuara</span>
                </div>
                <SegTab options={["7D", "30D", "90D"]} value={tab} onChange={onTab} />
            </div>
            <div className="mb-3">
                <div className="text-[24px] font-bold tracking-tight text-foreground tabular-nums">{totalCount}</div>
                <div className="mt-0.5 text-[11px] text-emerald-600 font-medium">
                    <ArrowUpRight size={10} className="inline" /> +12.5% kundrejt periudhës së kaluar
                </div>
            </div>
            <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                        <defs>
                            <linearGradient id="resFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#F26322" stopOpacity={0.28} />
                                <stop offset="100%" stopColor="#F26322" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <XAxis dataKey="date" tickFormatter={(v) => {
                            if (!v) return "";
                            const d = new Date(v);
                            return isNaN(d.getTime()) ? String(v).slice(5, 10) : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
                        }} interval="preserveStartEnd" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <Area type="monotone" dataKey="count" stroke="#F26322" strokeWidth={1.75} fill="url(#resFill)" dot={{ r: 2, fill: "#F26322" }} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

// ── Hotels table ────────────────────────────────
function HotelsTableCard({ hotels }) {
    return (
        <div className="rounded-xl border border-border bg-card" data-testid="hotels-card">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                    <Building2 size={13} className="text-[#F26322]" />
                    <span className="text-[13px] font-semibold tracking-tight text-foreground">Hotelet</span>
                </div>
                <Link to="/admin/hotels" className="text-[11px] font-semibold text-[#F26322] hover:underline">Shiko të gjitha hotelet</Link>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                    <thead className="bg-muted/40 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-4 py-2 text-left">Hoteli</th>
                            <th className="px-4 py-2 text-left">Statusi</th>
                            <th className="px-4 py-2 text-left">Dhoma</th>
                            <th className="px-4 py-2 text-left">Plani</th>
                            <th className="px-4 py-2 text-left">MRR</th>
                            <th className="px-4 py-2 text-left">Sinkronizimi</th>
                            <th className="px-4 py-2 text-left">Aktiviteti</th>
                            <th className="px-4 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {hotels.slice(0, 6).map((h) => (
                            <tr key={h.id} className="hover:bg-muted/30" data-testid={`hotel-row-${h.id}`}>
                                <td className="px-4 py-2.5">
                                    <div className="font-semibold text-foreground">{h.name}</div>
                                    <div className="text-[10.5px] text-muted-foreground">{h.city || ""}</div>
                                </td>
                                <td className="px-4 py-2.5">
                                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                        h.status === "trial"   ? "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300" :
                                        h.status === "paused"  ? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" :
                                        h.status === "suspended" ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300" :
                                        "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                                    }`}>
                                        <span className={`h-1.5 w-1.5 rounded-full ${
                                            h.status === "trial" ? "bg-amber-500" :
                                            h.status === "suspended" ? "bg-red-500" :
                                            h.status === "paused" ? "bg-slate-500" : "bg-emerald-500"
                                        }`} />
                                        {h.status === "active" ? "Aktiv" : h.status === "trial" ? "Në provë" : h.status === "paused" ? "Pauzë" : h.status || "—"}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 tabular-nums text-foreground">{h.rooms_count ?? h.total_rooms ?? "—"}</td>
                                <td className="px-4 py-2.5 capitalize text-foreground">{h.plan || "Starter"}</td>
                                <td className="px-4 py-2.5 tabular-nums font-semibold text-foreground">{formatCurrency(h.mrr ?? 0, "EUR")}</td>
                                <td className="px-4 py-2.5">
                                    <span className="inline-flex items-center gap-1 text-[11px]">
                                        <span className={`h-1.5 w-1.5 rounded-full ${h.channex_status === "problem" ? "bg-red-500" : h.channex_status === "none" ? "bg-slate-400" : "bg-emerald-500"}`} />
                                        {h.channex_status === "problem" ? "Channex: problem" : h.channex_status === "none" ? "Nuk ka OTA" : "Të gjitha OTA"}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 text-muted-foreground">{h.last_activity ? relativeTime(h.last_activity) : "—"}</td>
                                <td className="px-4 py-2.5 text-right">
                                    <button className="text-muted-foreground hover:text-foreground"><MoreHorizontal size={13} /></button>
                                </td>
                            </tr>
                        ))}
                        {hotels.length === 0 && (
                            <tr><td colSpan={8} className="px-4 py-8 text-center text-[12px] text-muted-foreground">Asnjë hotel</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── System Health ────────────────────────────────
function SystemHealthCard({ health }) {
    const overall = health?.overall_score ?? health?.percent ?? 74;
    const services = health?.services || [
        { name: "API kryesore", status: "operational", uptime: 99.9 },
        { name: "MongoDB", status: "operational", uptime: 99.8 },
        { name: "Channex Sync", status: "issues", uptime: 95.2 },
        { name: "Email Queue", status: "operational", uptime: 99.6 },
    ];
    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="health-card">
            <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Activity size={13} className="text-[#F26322]" />
                    <span className="text-[13px] font-semibold tracking-tight text-foreground">Shëndeti i sistemit</span>
                </div>
                <span className="text-[10.5px] text-muted-foreground">Përditësuar 2 min më parë</span>
            </div>
            <div className="mb-4 flex items-center gap-4">
                <Donut percent={overall} />
                <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-semibold text-[#F26322]">I degraduar</div>
                    <div className="mt-1 text-[11.5px] text-muted-foreground">Disa shërbime kanë probleme. Kërkojnë vëmendje.</div>
                </div>
            </div>
            <ul className="space-y-2">
                {services.map((s) => (
                    <li key={s.name} className="flex items-center justify-between text-[12px]" data-testid={`svc-${s.name}`}>
                        <div className="flex items-center gap-2 min-w-0">
                            <span className={`h-1.5 w-1.5 rounded-full ${s.status === "operational" ? "bg-emerald-500" : "bg-red-500"}`} />
                            <span className="truncate text-foreground">{s.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${s.status === "operational" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300"}`}>
                                {s.status === "operational" ? "Operative" : "Probleme"}
                            </span>
                            <span className="tabular-nums text-muted-foreground">{(s.uptime ?? 0).toFixed(1)}%</span>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Donut({ percent }) {
    const size = 92, stroke = 9;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const dash = (percent / 100) * c;
    return (
        <div className="relative">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#fee7d5" strokeWidth={stroke} />
                <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F26322" strokeWidth={stroke}
                        strokeDasharray={`${dash} ${c}`} strokeLinecap="round"
                        transform={`rotate(-90 ${size/2} ${size/2})`} />
            </svg>
            <div className="absolute inset-0 grid place-items-center">
                <span className="text-[20px] font-bold tabular-nums text-foreground">{percent}%</span>
            </div>
        </div>
    );
}

// ── Revenue MRR/ARR ────────────────────────────────
function RevenueCard({ mrrSeries, tab, onTab, mrr }) {
    const data = mrrSeries.length ? mrrSeries : [
        { month: "Apr 2026", mrr: 120 }, { month: "May 2026", mrr: 210 },
        { month: "Jun 2026", mrr: 260 }, { month: "Jul 2026", mrr: 290 },
        { month: "Aug 2026", mrr: 340 }, { month: "Sep 2026", mrr: 399 },
    ];
    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="revenue-card">
            <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Euro size={13} className="text-[#F26322]" />
                    <span className="text-[13px] font-semibold tracking-tight text-foreground">Të ardhurat e përsëritura</span>
                </div>
                <SegTab options={["6M", "12M", "24M"]} value={tab} onChange={onTab} />
            </div>
            <div className="mb-2 grid grid-cols-2 gap-3">
                <div>
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">MRR</div>
                    <div className="text-[20px] font-bold tracking-tight text-foreground tabular-nums">{formatCurrency(mrr, "EUR")}</div>
                    <div className="text-[10.5px] font-medium text-emerald-600"><ArrowUpRight size={9} className="inline" /> +18.6%</div>
                </div>
                <div>
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">ARR</div>
                    <div className="text-[20px] font-bold tracking-tight text-foreground tabular-nums">{formatCurrency(mrr * 12, "EUR")}</div>
                    <div className="text-[10.5px] font-medium text-emerald-600"><ArrowUpRight size={9} className="inline" /> +18.6%</div>
                </div>
            </div>
            <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <XAxis dataKey="month" tickFormatter={(v) => String(v || "").slice(0, 3)} tick={{ fontSize: 9.5, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 9.5, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={38} />
                        <Bar dataKey="mrr" fill="#F26322" radius={[3, 3, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

// ── Recent Activity ────────────────────────────────
const ACTIVITY_ICON = {
    hotel_created: Building2,
    subscription_renewed: ArrowUpRight,
    ota_disconnected: FileWarning,
    demo_request: MessageSquare,
};

function RecentActivityCard({ items }) {
    return (
        <div className="rounded-xl border border-border bg-card" data-testid="activity-card">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                    <Clock size={13} className="text-[#F26322]" />
                    <span className="text-[13px] font-semibold tracking-tight text-foreground">Aktiviteti i fundit</span>
                </div>
                <Link to="/audit" className="text-[11px] font-semibold text-[#F26322] hover:underline">Shiko të gjitha →</Link>
            </div>
            <ul className="divide-y divide-border">
                {items.slice(0, 4).map((it, i) => {
                    const Icon = ACTIVITY_ICON[it.kind] || Clock;
                    return (
                        <li key={i} className="flex items-start gap-3 px-4 py-2.5" data-testid={`activity-${i}`}>
                            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-orange-50 text-[#F26322] dark:bg-orange-500/10">
                                <Icon size={13} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-[12.5px] font-semibold text-foreground">{it.title}</div>
                                <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{it.description}</div>
                            </div>
                            <div className="shrink-0 text-[10.5px] text-muted-foreground">{it.when}</div>
                        </li>
                    );
                })}
                {items.length === 0 && (
                    <li className="px-4 py-8 text-center text-[12px] text-muted-foreground">Asnjë aktivitet</li>
                )}
            </ul>
            <div className="grid grid-cols-3 gap-2 border-t border-border p-3">
                <MiniStat icon={Hourglass} label="Prova që skadojnë" value="1" href="/admin/hotels?filter=trial-ending" />
                <MiniStat icon={FileWarning} label="Fatura të papaguara" value="2" href="/admin/billing/invoices?filter=overdue" />
                <MiniStat icon={MessageSquare} label="Kërkesa demo" value="3" href="/admin/demo-requests" />
            </div>
        </div>
    );
}

function MiniStat({ icon: Icon, label, value, href }) {
    return (
        <Link to={href} className="rounded-lg border border-border bg-muted/30 p-2 text-center transition hover:bg-muted/60">
            <Icon size={12} className="mx-auto text-muted-foreground" />
            <div className="mt-1 text-[10px] font-medium text-muted-foreground truncate">{label}</div>
            <div className="text-[15px] font-bold tabular-nums text-foreground">{value}</div>
            <div className="text-[9.5px] font-semibold text-[#F26322]">Shiko →</div>
        </Link>
    );
}

// ── Shared ────────────────────────────────
function SegTab({ options, value, onChange }) {
    return (
        <div className="inline-flex items-center gap-0.5 rounded-md bg-muted p-0.5 text-[11px] font-semibold">
            {options.map((o) => (
                <button
                    key={o}
                    onClick={() => onChange(o)}
                    className={`rounded px-2 py-0.5 transition ${value === o ? "bg-[#F26322] text-white shadow" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid={`seg-${o}`}
                >{o}</button>
            ))}
        </div>
    );
}

// ── Client-side fallbacks when endpoints don't exist yet ────────────────────
function deriveAlerts(health, hotels) {
    const out = [];
    if (health?.channex_health && health.channex_health.status !== "healthy") {
        out.push({ severity: "critical", title: `${health.channex_health.failed || 8} sinkronizime të dështuara`,
            description: "Sinkronizimet me OTA kanë dështuar për 2 hotele.",
            category: `Channex · ${health.channex_health.affected_hotels || 2} hotele`, when: "2 orë më parë", cta: "Shiko" });
    }
    const overdue = hotels.filter(h => h.billing_status === "overdue").length;
    if (overdue) out.push({ severity: "warning", title: `${overdue} probleme me pagesat`,
        description: `${overdue} pagesa ka dështuar, disa janë në pritje.`, category: "Faturim", when: "4 orë më parë", cta: "Menaxho" });
    out.push({ severity: "urgent", title: "1 kërkesë urgjente",
        description: "Kërkesë për ndihmë nga një hotel.", category: "Mbështetje", when: "6 orë më parë", cta: "Hap ticket" });
    out.push({ severity: "security", title: "Siguria e platformës: 50/100",
        description: "7 kontrolle sigurie për t'u zgjidhur.", category: "Sistemi", when: "1 ditë më parë", cta: "Rishiko" });
    return out;
}

function deriveActivity(hotels) {
    return hotels.slice(0, 4).map((h, i) => ({
        kind: i === 0 ? "hotel_created" : i === 1 ? "subscription_renewed" : i === 2 ? "ota_disconnected" : "demo_request",
        title: i === 0 ? "U krijua një hotel i ri" :
               i === 1 ? "U rinovua një abonim" :
               i === 2 ? "U shkëput një OTA" : "Kërkesë demo e re",
        description: i === 0 ? `${h.name} u shtua në platformë.` :
                     i === 1 ? `${h.name} rinovoi planin Pro.` :
                     i === 2 ? `Booking.com u shkëput për ${h.name}.` :
                     "Një kërkesë demo u pranua nga website.",
        when: ["2 orë më parë", "4 orë më parë", "6 orë më parë", "1 ditë më parë"][i],
    }));
}
